import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import GenericTable from "@components/tables/GenericTable";
import { BASE_URL } from "@utils/constants";
import { fetchGAAdminFolders, fetchGAAdminProjects } from "@store/thunks/gaadminThunk";
import { setSelectedAssignedProjectId } from "@store/slices/gaAdminSlice";

const ProjectDetailTableGAAdmin = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { assignedProjects } = useSelector((store) => store.gaAdmin);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [inputValue, setInputValue] = useState("");
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [hasNextPage, setHasNextPage] = useState(true);
    const [allProjects, setAllProjects] = useState([]);
    const [popup, setPopup] = useState({ isOpen: false, type: "", message: "" });

    const handleInputChange = (e) => {
        const newValue = e.target.value;
        setInputValue(newValue);
        setSearchQuery(newValue);
        setPageIndex(0);
        setHasNextPage(true);
    };

    const handlePopupClose = () => {
        setPopup({ isOpen: false, type: "", message: "" });
    };


    useEffect(() => {
        const fetchData = async () => {
            try {
                setInitialLoading(true);
                const res = await dispatch(fetchGAAdminProjects(searchQuery, pageIndex, pageSize));
                console.log("fetchGAAdminProjects response:", res);
                if (Array.isArray(res)) {
                    setAllProjects(res)
                    if (res.length < pageSize) {
                        setHasNextPage(false);
                    } else {
                        setHasNextPage(true);
                    }
                } else {
                    setAllProjects([]);
                    setHasNextPage(false);
                }
                setInitialLoading(false);
            } catch (error) {
                console.error("Error fetching projects:", error);
                setError("Failed to fetch projects: " + error.message);
                setInitialLoading(false);
            }
        };
        fetchData();
    }, [dispatch, searchQuery, pageIndex, pageSize]);

    // Compute paginated data
    const paginatedProjects = useMemo(() => {
        const start = pageIndex * pageSize;
        const end = start + pageSize;
        console.log("paginatedProjects:", allProjects.slice(start, end));
        return allProjects.slice(start, end);
    }, [allProjects, pageIndex, pageSize]);

    // Update hasNextPage for client-side fallback
    useEffect(() => {
        console.log("allProjects:", allProjects);
        setHasNextPage((pageIndex + 1) * pageSize < allProjects.length);
    }, [allProjects, pageIndex, pageSize]);

    const handleProjectClick = async ({ row }) => {
        try {
            setLoading(true);
            const projectId = row.original.projectId;
            dispatch(setSelectedAssignedProjectId(projectId));
            await dispatch(fetchGAAdminFolders(projectId));
            navigate("/GACSAdmin/folders");
        } catch (error) {
            console.error("Error handling project click:", error);
            setError("Failed to fetch folders: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    const projectColumns = [
        {
            id: "serial_number",
            header: () => "Sr. No.",
            cell: ({ row }) => (
                <div className="text-center">
                    {pageIndex * pageSize + row.index + 1}
                </div>
            ),
            cellClassName: "text-center",
        },
        {
            id: "projectName",
            header: () => "Project Name",
            accessorKey: "projectName",
            cell: ({ row }) => (
                <button
                    onClick={() => handleProjectClick({ row })}
                    className="text-[#ec7026] hover:text-[#d85d1a] focus:underline"
                >
                    {row.original.projectName}
                </button>
            ),
        },
        {
            id: "description",
            header: () => "Description",
            accessorKey: "description",
            cell: ({ row }) => (
                <div className="max-w-md">
                    {row.original.description || "No description available"}
                </div>
            ),
        },
        {
            id: "createdAt",
            header: () => "Created At",
            accessorKey: "createdAt",
            cell: ({ row }) => {
                const deadline = row.original?.createdAt || row.createdAt;
                if (!deadline) return <div>N/A</div>;
                try {
                    const date = new Date(deadline);
                    const now = new Date();
                    const isOverdue = date < now;
                    const isApproaching = !isOverdue && date - now < 7 * 24 * 60 * 60 * 1000;
                    return (
                        <div>
                            <div
                                className={
                                    isOverdue
                                        ? "text-red-600"
                                        : isApproaching
                                            ? "text-amber-600"
                                            : null
                                }
                            >
                                {date.toLocaleDateString("en-US", {
                                    year: "numeric",
                                    month: "long",
                                    day: "numeric",
                                })}
                            </div>
                            <div className="text-sm text-gray-500">
                                {date.toLocaleTimeString("en-US", {
                                    hour: "numeric",
                                    minute: "numeric",
                                    hour12: true,
                                })}
                            </div>
                        </div>
                    );
                } catch (e) {
                    return <div>{deadline}</div>;
                }
            },
            cellClassName: "text-center",
        },
        {
            id: "deadline",
            header: () => "Deadline",
            accessorKey: "deadline",
            cell: ({ row }) => {
                const deadline = row.original?.deadline || row.deadline;
                if (!deadline) return <div>N/A</div>;
                try {
                    const date = new Date(deadline);
                    const now = new Date();
                    const isOverdue = date < now;
                    const isApproaching = !isOverdue && date - now < 7 * 24 * 60 * 60 * 1000;
                    return (
                        <div>
                            <div
                                className={
                                    isOverdue
                                        ? "text-red-600"
                                        : isApproaching
                                            ? "text-amber-600"
                                            : null
                                }
                            >
                                {date.toLocaleDateString("en-US", {
                                    year: "numeric",
                                    month: "long",
                                    day: "numeric",
                                })}
                            </div>
                            <div className="text-sm text-gray-500">
                                {date.toLocaleTimeString("en-US", {
                                    hour: "numeric",
                                    minute: "numeric",
                                    hour12: true,
                                })}
                            </div>
                        </div>
                    );
                } catch (e) {
                    return <div>{deadline}</div>;
                }
            },
            cellClassName: "text-center",
        },
    ];

    return (
        <div className="flex flex-col min-h-[calc(100vh-4rem)] bg-gray-50/50">
            <div className="lg:pr-8 lg:pt-8 flex justify-end items-center">
                <div className="relative w-full sm:w-64">
                    <input
                        type="text"
                        placeholder="Search projects..."
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#ec7026] text-base font-['Semantic/Strong'] text-gray-700 pl-10"
                        value={inputValue}
                        onChange={handleInputChange}
                    />
                    <svg
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                    </svg>
                </div>
            </div>
            {popup.isOpen && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-w-md w-full mx-4">
                        <h2 className="text-lg font-['Semantic/Strong'] font-bold text-gray-900">
                            {popup.type === "error" ? "Error" : "Info"}
                        </h2>
                        <p className="mt-2 text-sm text-gray-500 font-['Semantic/Strong']">
                            {popup.message}
                        </p>
                        <div className="mt-4 flex justify-end">
                            <button
                                onClick={handlePopupClose}
                                className="inline-flex items-center px-4 py-2 border border-[#ec7026] shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-white bg-[#ec7026] hover:bg-[#d85d1a] focus:outline-none focus:ring-2 focus:ring-[#ec7026]"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <div className="w-full p-4 sm:p-6 lg:p-8">
                {initialLoading ? (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
                        <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
                        <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
                    </div>
                ) : error ? (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                            <p>{error}</p>
                            <p className="mt-2">Please try again later or contact support.</p>
                        </div>
                    </div>
                ) : (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                        <div className="w-full overflow-x-auto">
                            {paginatedProjects.length === 0 ? (
                                <div className="p-6">
                                    <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded">
                                        <p>{searchQuery ? "No projects match your search" : "No projects assigned yet."}</p>
                                        <p className="mt-2">
                                            {searchQuery
                                                ? "Try adjusting your search term."
                                                : "You have not been assigned any projects for validation. Please contact the administrator if you believe this is an error."}
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <GenericTable
                                    data={paginatedProjects}
                                    columns={projectColumns}
                                    showPagination={true}
                                    manualPagination={true}
                                    pageIndex={pageIndex}
                                    pageSize={pageSize}
                                    hasNextPage={hasNextPage}
                                    onPaginationChange={({ pageIndex, pageSize }) => {
                                        setPageIndex(pageIndex);
                                        setPageSize(pageSize);
                                    }}
                                    emptyMessage={searchQuery ? "No projects match your search" : "No GA projects available"}
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProjectDetailTableGAAdmin;